import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'

@customElement('schmancy-spinner')
export default class SchmnacySpinner extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: inline-block;
				color: inherit;
			}

			:host([color='primary']) {
				color: var(--schmancy-sys-color-primary-default);
			}

			:host([color='on-primary']) {
				color: var(--schmancy-sys-color-primary-on);
			}

			:host([color='secondary']) {
				color: var(--schmancy-sys-color-secondary-default);
			}

			:host([color='on-secondary']) {
				color: var(--schmancy-sys-color-secondary-on);
			}

			:host([color='tertiary']) {
				color: var(--schmancy-sys-color-tertiary-default);
			}

			:host([color='on-tertiary']) {
				color: var(--schmancy-sys-color-tertiary-on);
			}

			:host([color='error']) {
				color: var(--schmancy-sys-color-error-default);
			}

			:host([color='on-error']) {
				color: var(--schmancy-sys-color-error-on);
			}

			:host([color='success']) {
				color: var(--schmancy-sys-color-success-default);
			}

			:host([color='on-success']) {
				color: var(--schmancy-sys-color-success-on);
			}

			:host([color='surface']) {
				color: var(--schmancy-sys-color-surface-default);
			}

			:host([color='on-surface']) {
				color: var(--schmancy-sys-color-surface-on);
			}

			/* The surface family has no "variant" ground: M3 retired surfaceVariant
			   in favour of the container ladder, and this theme's generator emits
			   exactly one role at that tone — surfaceContainerHighest (which the
			   family already aliases as "surface-highest"). Registering a 15th
			   surface role to satisfy one enum value would invent a tone the
			   generator never produces; collapsing the value onto "onVariant"
			   would make it a synonym of the "on-surface-variant" sibling right
			   below and lose the ground/ink distinction the whole enum is built
			   on. So the enum value retargets onto the ground it always meant. */
			:host([color='surface-variant']) {
				color: var(--schmancy-sys-color-surface-containerHighest);
			}

			:host([color='on-surface-variant']) {
				color: var(--schmancy-sys-color-surface-onVariant);
			}

			.spinner {
				animation: spin 1s linear infinite;
				animation-direction: reverse;
			}

			@keyframes spin {
				from {
					transform: rotate(0deg);
				}
				to {
					transform: rotate(360deg);
				}
			}

			/* Reduced motion stills the rotation. The ring, its darker arc and the
			   centre dot still read as work in progress, since an idle control shows
			   none of them. */
			@media (prefers-reduced-motion: reduce) {
				.spinner {
					animation: none;
				}
			}
		`,
	]
	@property({ type: String, reflect: true })
	color?:
		| 'primary'
		| 'on-primary'
		| 'secondary'
		| 'on-secondary'
		| 'tertiary'
		| 'on-tertiary'
		| 'error'
		| 'on-error'
		| 'success'
		| 'on-success'
		| 'surface'
		| 'on-surface'
		| 'surface-variant'
		| 'on-surface-variant'

	/** Token: 'xxs' 12px, 'xs' 16px, 'sm' 20px, 'md' 24px, 'lg' 32px. Numeric: Tailwind units (each = 4px). */
	@property() size: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | number = 'md'
	@property({ type: Boolean }) glass: boolean = false

	protected render(): unknown {
		const tokenSizes: Record<string, number> = {
			xxs: 12,
			xs: 16,
			sm: 20,
			md: 24,
			lg: 32,
		}

		let sizeInPx: number
		if (typeof this.size === 'string' && this.size in tokenSizes) {
			sizeInPx = tokenSizes[this.size]
		} else if (typeof this.size === 'number' && !isNaN(this.size)) {
			sizeInPx = this.size * 4
		} else {
			sizeInPx = 24
		}

		const style = {
			width: `${sizeInPx}px`,
			height: `${sizeInPx}px`,
		}

		return this.glass
			? html`
					<div class="spinner relative" style=${this.styleMap(style)}>
						<div
							class="absolute inset-0 rounded-full backdrop-blur-[var(--schmancy-sys-glass-blur)] backdrop-saturate-[var(--schmancy-sys-glass-saturate)]
							bg-surface-container/20
							shadow-[inset_0_1px_1px_0_rgba(255,255,255,0.2)]
							border border-[var(--schmancy-sys-glass-border)]"
						></div>

						<svg fill="none" viewBox="0 0 16 16" aria-hidden="true" role="img" class="relative z-10 w-full h-full">
							<path opacity=".7" d="M8 15A7 7 0 108 1a7 7 0 000 14v0z" stroke="currentColor" stroke-width="2"></path>
							<path d="M15 8a7 7 0 01-7 7" stroke="currentColor" stroke-width="2"></path>
							<path d="M8 12a4 4 0 100-8 4 4 0 000 8z" fill="currentColor" opacity="0.8"></path>
						</svg>
					</div>
				`
			: html`
					<div class="spinner" style=${this.styleMap(style)}>
						<svg fill="none" viewBox="0 0 16 16" aria-hidden="true" role="img" class="w-full h-full">
							<path opacity=".5" d="M8 15A7 7 0 108 1a7 7 0 000 14v0z" stroke="currentColor" stroke-width="2"></path>
							<path d="M15 8a7 7 0 01-7 7" stroke="currentColor" stroke-width="2"></path>
							<path d="M8 12a4 4 0 100-8 4 4 0 000 8z" fill="currentColor"></path>
						</svg>
					</div>
				`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-spinner': SchmnacySpinner
	}
}
